import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import clsx from 'clsx'
import type { FavoriteKind } from 'api/AccountApi'
import { useUser } from 'hooks/user'
import { useState, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import getTestId from 'utils/getTestId'

import style from './FavoriteStar.module.css'

const FavoriteStar = ({ kind, id }: { kind: FavoriteKind; id: number }) => {
  const { t } = useTranslation()
  const user = useUser()
  const [pending, setPending] = useState(false)
  const ids =
    kind === 'robot'
      ? user?.state.favoriteRobotIds
      : kind === 'facility'
        ? user?.state.favoriteFacilityIds
        : kind === 'station'
          ? user?.state.favoriteStationIds
          : user?.state.favoriteOrganizationIds
  const active = Boolean(ids?.includes(id))
  const label = active ? t('favorites.remove') : t('favorites.add')

  const onClick = async (event: MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
    if (!user || pending) return
    setPending(true)
    try {
      await user.actions.toggleFavorite(kind, id)
    } catch (error) {
      console.error('Failed to update favorite', error)
    } finally {
      setPending(false)
    }
  }

  return (
    <button
      type="button"
      className={clsx(style.button, { [style.active]: active })}
      aria-pressed={active}
      aria-label={label}
      title={label}
      disabled={pending}
      onClick={onClick}
      data-test-id={getTestId('favorite-toggle')}
    >
      {active ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
    </button>
  )
}

export default FavoriteStar
